<template>
    <form
        class="space-y-6"
        @submit.prevent="submit"
    >
        <h3 class="subtitle">
            Modifier la journée de {{ staff.first_name }} {{ staff.last_name }}
        </h3>

        <div class="pb-6 border-b">
            <UiSelect
                name="staff_schedule_uuid"
                :options="staffSchedulesOptions"
                label="Pré remplir selon un horaire type"
                @update:model-value="setScheduleFromStaffSchedule"
            />
        </div>

        <UiSelect
            v-model="formData.type"
            label="Type"
            name="type"
            :options="typeOptions"
        />

        <UiSelect
            v-model="formData.extra_type"
            label="Sous Type"
            name="type"
            :options="extraTypeOptions"
        />

        <div class="grid grid-cols-2 gap-6">
            <UiInput
                v-model="formData.should_start_at"
                label="Arrive à"
                type="time"
                name="should_start_at"
                required
                placeholder="Arrive à"
            />
            <UiInput
                v-model="formData.should_left_at"
                type="time"
                name="should_left_at"
                label="Part à"
                required
                placeholder="Part à"
            />
        </div>
        <div class="p-3 space-y-6 border rounded">
            <div class="grid grid-cols-2 gap-6">
                <UiInput
                    v-model="formData.start_at"
                    label="Arrivée réelle"
                    type="time"
                    name="start_at"
                    placeholder="Arrivée réelle"
                />
                <UiInput
                    v-model="formData.left_at"
                    label="Départ réel"
                    type="time"
                    name="left_at"
                    placeholder="Départ réel"
                />
            </div>
        </div>
        <div
            v-if="formData.type <= 2"
            class="grid grid-cols-2 gap-6"
        >
            <UiInput
                v-model="formData.pause_start_at"
                label="Pause démarre à"
                :errors="errors"
                name="pause_start_at"
                type="time"
            />
            <UiInput
                v-model="formData.pause_stop_at"
                label="Pause fini à"
                :errors="errors"
                name="pause_stop_at"
                type="time"
            />
        </div>

        <UiSelect
            v-model="formData.classroom_uuid"
            name="classroom_uuid"
            :errors="errors"
            label="Attribuer à une section"
            :options="classroomOptions"
        />

        <UiTextarea
            v-model="formData.note"
            label="Remarques"
            name="note"
            placeholder="Écrivez ici"
        />

        <div class="flex justify-between">
            <button
                type="button"
                class="btn btn-red"
                :class="{ loading: isLoading }"
                :disabled="isLoading"
                @click="removeAttendance"
            >
                Supprimer
            </button>
            <button
                type="submit"
                class="btn btn-primary"
                :class="{ loading: isLoading }"
                :disabled="isLoading"
            >
                Sauvegarder
            </button>
        </div>
    </form>
</template>

<script>
import dayjs from 'dayjs'
import { extraTypes, types } from 'abcreche-constants/src/const/staffs/attendances/types'
import { dateRangeMixin, loadingMixin } from 'abcreche-ui'
import classroomOptions from '~~/mixins/classrooms/classroomOptions'
import StaffSchedulesRepository from '~~/repositories/Creche/StaffSchedulesRepository.js'
import StaffAttendanceRepository from '~~/repositories/Creche/StaffAttendanceRepository.js'

export default {
    mixins: [loadingMixin, dateRangeMixin, classroomOptions],

    props: {
        staff: {
            type: Object,
            required: true
        },
        staffAttendance: {
            type: Object,
            required: true
        }
    },

    emits: ['updated', 'deleted'],

    data () {
        return {
            formData: {
                type: this.staffAttendance.type,
                extra_type: this.staffAttendance.extra_type,
                note: this.staffAttendance.note,
                should_start_at: dayjs(this.staffAttendance.should_start_at).format('HH:mm'),
                should_left_at: dayjs(this.staffAttendance.should_left_at).format('HH:mm'),
                start_at: this.staffAttendance.start_at ? dayjs(this.staffAttendance.start_at).format('HH:mm') : null,
                left_at: this.staffAttendance.left_at ? dayjs(this.staffAttendance.left_at).format('HH:mm') : null,
                pause_start_at: this.staffAttendance.pause_start_at,
                pause_stop_at: this.staffAttendance.pause_stop_at,
                classroom_uuid: this.staffAttendance.classroom?.uuid
            },
            staffSchedules: []
        }
    },

    computed: {
        typeOptions: types,
        extraTypeOptions: extraTypes,
        quarterHours () {
            return this.generateDateRange(
                dayjs().hour(6).minute(0),
                dayjs().hour(19).minute(59),
                15,
                'minutes',
                'HH:mm'
            )
        },
        staffSchedulesOptions () {
            return this.staffSchedules.map((schedule) => {
                if (schedule.pause_start_at && schedule.pause_stop_at) {
                    return {
                        value: schedule.uuid,
                        label: schedule.name + ' (' + schedule.from.slice(0, -3) + ' - ' + schedule.to.slice(0, -3) + ')' + ' (pause : ' + schedule.pause_start_at.slice(0, -3) + ' - ' + schedule.pause_stop_at.slice(0, -3) + ')'
                    }
                }
                return {
                    value: schedule.uuid,
                    label: schedule.name + ' (' + schedule.from.slice(0, -3) + ' - ' + schedule.to.slice(0, -3) + ')'
                }
            })
        }
    },

    created () {
        this.fetchClassrooms()
            .catch(error => this.pageError(error))
        this.getStaffSchedules()
    },

    methods: {
        getStaffSchedules () {
            StaffSchedulesRepository.index()
                .then((response) => {
                    this.staffSchedules = response.data
                })
                .catch(error => this.pageError(error))
        },
        setScheduleFromStaffSchedule (value) {
            const schedule = this.staffSchedules.find(s => s.uuid === value)

            this.formData.should_start_at = schedule.from.slice(0, -3)
            this.formData.should_left_at = schedule.to.slice(0, -3)
            this.formData.pause_start_at = schedule.pause_start_at ? schedule.pause_start_at.slice(0, -3) : null
            this.formData.pause_stop_at = schedule.pause_stop_at ? schedule.pause_stop_at.slice(0, -3) : null
        },

        submit () {
            this.load()

            const data = { ...this.formData }
            data.should_start_at = this.staffAttendance.date + ' ' + this.formData.should_start_at
            data.should_left_at = this.staffAttendance.date + ' ' + this.formData.should_left_at

            if (this.formData.start_at) {
                data.start_at = this.staffAttendance.date + ' ' + this.formData.start_at
            }
            if (this.formData.left_at) {
                data.left_at = this.staffAttendance.date + ' ' + this.formData.left_at
            }

            StaffAttendanceRepository.update(this.staff.uuid, this.staffAttendance.uuid, data)
                .then(() => {
                    this.$emit('updated')
                    this.done('La journée a été correctement modifiée !')
                })
                .catch(error => this.doneWithErrors(error.response.data))
        },

        removeAttendance () {
            this.load()

            StaffAttendanceRepository.delete(this.staff.uuid, this.staffAttendance.uuid)
                .then(() => {
                    this.$emit('deleted')
                    this.done('La journée a été correctement supprimée !')
                })
                .catch(error => this.doneWithErrors(error))
        },

        changeHours (h) {
            this.formData.should_start_at = h[0]
            this.formData.should_left_at = h[1]
        },

        changeHoursReal (h) {
            this.formData.start_at = h[0]
            this.formData.left_at = h[1]
        }
    }
}
</script>
